import Phaser from 'phaser';
import type {
  GameRuntime,
  GameRuntimeConfig,
  GameRuntimeEvents,
  GameRuntimeState,
} from '@adgf/game-engine';

const COLOR_BG = 0x1a1a2e;
const COLOR_GROUND = 0x2d2d44;
const COLOR_TEXT = '#ffffff';
const COLOR_TEXT_DIM = '#8888aa';
const COLOR_ACCENT = 0xff6b9d;

const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;
const DURATION_MS = 60000;

interface Target {
  id: number;
  x: number;
  y: number;
  radius: number;
  points: number;
  distance: 'near' | 'medium' | 'far';
  hp: number;
  gameObject: Phaser.GameObjects.Container | null;
  alive: boolean;
}

interface Upgrades {
  damage: number; // 1 = one-shot kill
  magazine: number; // bullets per magazine
  fireRate: number; // ms between shots
  scopeSize: number; // scope radius
}

interface SceneCallbacks {
  onShoot: () => void;
  onHit: (points: number) => void;
  onMiss: () => void;
  onGameOver: (score: number) => void;
  onStart: () => void;
  onUpgrade: (type: string, level: number) => void;
}

const DEFAULT_UPGRADES: Upgrades = {
  damage: 1,
  magazine: 5,
  fireRate: 1000,
  scopeSize: 120,
};

const UPGRADE_COSTS: Record<keyof Upgrades, number[]> = {
  damage: [0, 100, 250, 500],
  magazine: [0, 80, 160, 320],
  fireRate: [0, 120, 240, 480],
  scopeSize: [0, 60, 120, 240],
};

class SniperRangeScene extends Phaser.Scene {
  private targets: Target[] = [];
  private nextTargetId = 0;
  private cb: SceneCallbacks;
  private playing = false;
  private gameOver = false;
  private score = 0;
  private ammo = 5;
  private maxAmmo = 5;
  private timeLeft = DURATION_MS;
  private lastShotTime = 0;
  private upgrades: Upgrades = { ...DEFAULT_UPGRADES };
  private scopeCircle: Phaser.GameObjects.Graphics | null = null;
  private scopeCrosshair: Phaser.GameObjects.Graphics | null = null;
  private scoreText: Phaser.GameObjects.Text | null = null;
  private ammoText: Phaser.GameObjects.Text | null = null;
  private timerText: Phaser.GameObjects.Text | null = null;
  private muzzleFlash: Phaser.GameObjects.Arc | null = null;
  private startOverlay: Phaser.GameObjects.Container | null = null;
  private gameOverOverlay: Phaser.GameObjects.Container | null = null;

  constructor(cb: SceneCallbacks) {
    super('SniperRangeScene');
    this.cb = cb;
  }

  create(): void {
    this.cameras.main.setBackgroundColor(COLOR_BG);
    this.targets = [];
    this.nextTargetId = 0;
    this.playing = false;
    this.gameOver = false;
    this.score = 0;
    this.ammo = this.upgrades.magazine;
    this.maxAmmo = this.upgrades.magazine;
    this.timeLeft = DURATION_MS;
    this.lastShotTime = 0;

    this.buildBackground();
    this.buildHUD();
    this.buildScope();
    this.buildStartOverlay();
  }

  private buildBackground(): void {
    const { width, height } = this.scale;

    // Simple gradient sky
    for (let i = 0; i < 10; i++) {
      const y = (height / 2) * (i / 10);
      const shade = 30 + i * 5;
      const c = (shade << 16) | (shade << 8) | (shade + 20);
      this.add.rectangle(width / 2, y + height / 20, width, height / 10, c, 1).setDepth(-20);
    }

    // Ground
    this.add.rectangle(width / 2, height - 80, width, 160, COLOR_GROUND, 1).setDepth(-15);

    // Distant mountains
    this.add.rectangle(width * 0.2, height / 2 - 40, 200, 120, 0x252545, 0.6).setDepth(-18);
    this.add.rectangle(width * 0.7, height / 2 - 60, 250, 150, 0x252545, 0.6).setDepth(-18);
  }

  private buildHUD(): void {
    const { width } = this.scale;

    this.scoreText = this.add.text(20, 20, 'Score: 0', {
      fontSize: '22px', color: COLOR_TEXT, fontStyle: 'bold', fontFamily: 'Arial, sans-serif',
    });

    this.ammoText = this.add.text(width - 20, 20, `Ammo: ${this.ammo}/${this.maxAmmo}`, {
      fontSize: '18px', color: COLOR_TEXT_DIM, fontFamily: 'Arial, sans-serif',
    }).setOrigin(1, 0);

    this.timerText = this.add.text(width / 2, 20, '60s', {
      fontSize: '24px', color: '#ff6b9d', fontStyle: 'bold', fontFamily: 'Arial, sans-serif',
    }).setOrigin(0.5, 0);
  }

  private buildScope(): void {
    const { width, height } = this.scale;
    const scopeR = this.upgrades.scopeSize;

    // Scope background (dark circle)
    this.scopeCircle = this.add.graphics();
    this.scopeCircle.setDepth(500);
    this.drawScopeCircle(width / 2, height / 2, scopeR);

    // Crosshair
    this.scopeCrosshair = this.add.graphics();
    this.scopeCrosshair.setDepth(501);
    this.drawCrosshair(width / 2, height / 2, scopeR);

    // Muzzle flash (hidden by default)
    this.muzzleFlash = this.add.circle(0, 0, 20, 0xffff00, 0);
    this.muzzleFlash.setDepth(502);
  }

  private drawScopeCircle(x: number, y: number, r: number): void {
    if (!this.scopeCircle) return;
    this.scopeCircle.clear();
    // Dark overlay outside scope
    this.scopeCircle.fillStyle(0x000000, 0.7);
    this.scopeCircle.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    // Cut out the scope circle (use destination-out)
    // Actually, Phaser graphics doesn't support masking easily.
    // Instead, just draw the scope ring.
    this.scopeCircle.clear();
    this.scopeCircle.lineStyle(3, 0xffffff, 0.9);
    this.scopeCircle.strokeCircle(x, y, r);
    // Darken outside area with a big semi-transparent rect
    this.scopeCircle.fillStyle(0x000000, 0.5);
    this.scopeCircle.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
  }

  private drawCrosshair(x: number, y: number, r: number): void {
    if (!this.scopeCrosshair) return;
    this.scopeCrosshair.clear();
    this.scopeCrosshair.lineStyle(2, 0xff0000, 0.8);
    // Horizontal line
    this.scopeCrosshair.lineBetween(x - r, y, x + r, y);
    // Vertical line
    this.scopeCrosshair.lineBetween(x, y - r, x, y + r);
    // Center dot
    this.scopeCrosshair.fillStyle(0xff0000, 1);
    this.scopeCrosshair.fillCircle(x, y, 3);
  }

  private spawnTarget(): void {
    if (!this.playing) return;

    const { width, height } = this.scale;
    const roll = Math.random();

    let distance: 'near' | 'medium' | 'far';
    let radius: number;
    let points: number;

    if (roll < 0.4) {
      distance = 'near';
      radius = 35;
      points = 10;
    } else if (roll < 0.75) {
      distance = 'medium';
      radius = 25;
      points = 25;
    } else {
      distance = 'far';
      radius = 15;
      points = 50;
    }

    const x = Phaser.Math.Between(radius + 50, width - radius - 50);
    const y = Phaser.Math.Between(100, height - 150);

    const target: Target = {
      id: this.nextTargetId++,
      x, y, radius, points, distance,
      hp: this.upgrades.damage >= 2 ? 1 : 1,
      gameObject: null,
      alive: true,
    };

    // Create visual
    const container = this.add.container(x, y);
    container.setDepth(100);

    // Target: concentric circles
    const outer = this.add.circle(0, 0, radius, 0xff4444);
    const middle = this.add.circle(0, 0, radius * 0.66, 0xffffff);
    const inner = this.add.circle(0, 0, radius * 0.33, 0xff4444);

    container.add([outer, middle, inner]);
    target.gameObject = container;
    this.targets.push(target);

    // Auto-expire after 2.5 seconds
    this.time.delayedCall(2500, () => {
      if (target.alive && target.gameObject) {
        target.alive = false;
        target.gameObject.destroy();
      }
    });
  }

  private handleShoot(pointerX: number, pointerY: number): void {
    const now = this.time.now;
    if (now - this.lastShotTime < this.upgrades.fireRate) return;
    if (this.ammo <= 0) return;

    this.lastShotTime = now;
    this.ammo--;
    this.updateHUD();
    this.cb.onShoot();

    // Muzzle flash
    if (this.muzzleFlash) {
      this.muzzleFlash.setPosition(pointerX, pointerY);
      this.muzzleFlash.setFillStyle(0xffff00, 0.8);
      this.tweens.add({
        targets: this.muzzleFlash,
        alpha: 0,
        duration: 100,
        onComplete: () => this.muzzleFlash?.setFillStyle(0xffff00, 0),
      });
    }

    // Check if hit any target
    let hitSomething = false;
    for (const target of this.targets) {
      if (!target.alive || !target.gameObject) continue;
      const dist = Phaser.Math.Distance.Between(pointerX, pointerY, target.x, target.y);
      if (dist <= target.radius) {
        // Hit!
        target.hp--;
        if (target.hp <= 0) {
          target.alive = false;
          this.score += target.points;
          this.updateHUD();
          this.cb.onHit(target.points);

          // Hit effect
          this.tweens.add({
            targets: target.gameObject,
            scale: 1.5,
            alpha: 0,
            duration: 200,
            onComplete: () => target.gameObject?.destroy(),
          });
        }
        hitSomething = true;
        break;
      }
    }

    if (!hitSomething) {
      this.cb.onMiss();
    }

    // Reload if empty
    if (this.ammo === 0) {
      this.time.delayedCall(500, () => {
        this.ammo = this.maxAmmo;
        this.updateHUD();
      });
    }
  }

  private updateHUD(): void {
    this.scoreText?.setText(`Score: ${this.score}`);
    this.ammoText?.setText(`Ammo: ${this.ammo}/${this.maxAmmo}`);
  }

  private buildStartOverlay(): void {
    const { width, height } = this.scale;
    this.startOverlay = this.add.container(width / 2, height / 2);
    this.startOverlay.setDepth(1000);

    const bg = this.add.rectangle(0, 0, width, height, 0x0a0a15, 0.92);
    bg.setOrigin(0.5);
    this.startOverlay.add(bg);

    const title = this.add.text(0, -100, 'SHARPSHOOTER', {
      fontSize: '40px', color: COLOR_TEXT, fontStyle: 'bold',
      fontFamily: 'Arial, sans-serif', letterSpacing: 3,
    });
    title.setOrigin(0.5);
    this.startOverlay.add(title);

    const rules = this.add.text(0, -30, [
      'Move mouse to aim. Click to shoot.',
      'Close targets = 10 pts, Far = 50 pts.',
      'Earn points to buy upgrades.',
      '60 seconds on the clock!',
    ].join('\n'), {
      fontSize: '16px', color: COLOR_TEXT_DIM,
      fontFamily: 'Arial, sans-serif', align: 'center', lineSpacing: 8,
    });
    rules.setOrigin(0.5);
    this.startOverlay.add(rules);

    const btn = this.add.rectangle(0, 60, 220, 60, COLOR_ACCENT);
    btn.setStrokeStyle(3, 0xffffff, 0.2);
    const btnText = this.add.text(0, 60, 'START GAME', {
      fontSize: '22px', color: '#ffffff', fontStyle: 'bold', fontFamily: 'Arial, sans-serif',
    });
    btnText.setOrigin(0.5);
    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerdown', () => {
      this.startOverlay?.destroy();
      this.startGame();
    });
    this.startOverlay.add([btn, btnText]);
  }

  private buildGameOverOverlay(): void {
    const { width, height } = this.scale;
    this.gameOverOverlay = this.add.container(width / 2, height / 2);
    this.gameOverOverlay.setDepth(1000);

    const bg = this.add.rectangle(0, 0, width, height, 0x0a0a15, 0.92);
    bg.setOrigin(0.5);
    this.gameOverOverlay.add(bg);

    const title = this.add.text(0, -120, "TIME'S UP!", {
      fontSize: '36px', color: COLOR_TEXT, fontStyle: 'bold', fontFamily: 'Arial, sans-serif',
    });
    title.setOrigin(0.5);
    this.gameOverOverlay.add(title);

    const scoreText = this.add.text(0, -60, `Final Score: ${this.score}`, {
      fontSize: '28px', color: '#ff6b9d', fontStyle: 'bold', fontFamily: 'Arial, sans-serif',
    });
    scoreText.setOrigin(0.5);
    this.gameOverOverlay.add(scoreText);

    // Upgrades panel
    const upgradesY = -10;
    const upgradeLabels: { key: keyof Upgrades; label: string }[] = [
      { key: 'damage', label: 'Damage' },
      { key: 'magazine', label: 'Magazine' },
      { key: 'fireRate', label: 'Fire Rate' },
      { key: 'scopeSize', label: 'Scope Size' },
    ];

    const upTitle = this.add.text(0, upgradesY - 40, 'UPGRADES (click to buy)', {
      fontSize: '16px', color: COLOR_TEXT_DIM, fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
    });
    upTitle.setOrigin(0.5);
    this.gameOverOverlay.add(upTitle);

    for (let i = 0; i < upgradeLabels.length; i++) {
      const { key, label } = upgradeLabels[i];
      const rowY = upgradesY + i * 40;
      const level = this.upgrades[key];
      const cost = UPGRADE_COSTS[key][Math.min(level, 3)];
      const canAfford = this.score >= cost && level < 4;

      const rowBg = this.add.rectangle(0, rowY, 300, 32, canAfford ? 0x2d5a3d : 0x2a2a4a, 0.8);
      rowBg.setStrokeStyle(1, canAfford ? 0x4ade80 : 0x4a4a6a, 0.5);
      this.gameOverOverlay.add(rowBg);

      const labelText = this.add.text(-130, rowY, `${label} Lv${level}`, {
        fontSize: '14px', color: COLOR_TEXT, fontFamily: 'Arial, sans-serif',
      });
      labelText.setOrigin(0, 0.5);
      this.gameOverOverlay.add(labelText);

      const costText = this.add.text(130, rowY, level >= 4 ? 'MAX' : `${cost} pts`, {
        fontSize: '14px',
        color: canAfford ? '#4ade80' : '#666',
        fontFamily: 'Arial, sans-serif',
      });
      costText.setOrigin(1, 0.5);
      this.gameOverOverlay.add(costText);

      if (canAfford) {
        rowBg.setInteractive({ useHandCursor: true });
        rowBg.on('pointerdown', () => this.buyUpgrade(key, cost));
      }
    }

    // Play again button
    const btnY = 160;
    const btn = this.add.rectangle(0, btnY, 220, 56, COLOR_ACCENT);
    btn.setStrokeStyle(3, 0xffffff, 0.2);
    const btnText = this.add.text(0, btnY, 'PLAY AGAIN', {
      fontSize: '20px', color: '#ffffff', fontStyle: 'bold', fontFamily: 'Arial, sans-serif',
    });
    btnText.setOrigin(0.5);
    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerdown', () => this.scene.restart());
    this.gameOverOverlay.add([btn, btnText]);
  }

  private buyUpgrade(key: keyof Upgrades, cost: number): void {
    if (this.score < cost) return;
    if (this.upgrades[key] >= 4) return;

    this.score -= cost;
    this.upgrades[key]++;

    if (key === 'magazine') {
      this.maxAmmo = this.upgrades.magazine;
      this.ammo = this.maxAmmo;
    }

    this.updateHUD();
    this.cb.onUpgrade(key, this.upgrades[key]);

    // Refresh overlay
    this.gameOverOverlay?.destroy();
    this.buildGameOverOverlay();
  }

  private startGame(): void {
    this.playing = true;
    this.cb.onStart();

    // Spawn targets periodically
    this.time.addEvent({
      delay: 800,
      callback: () => this.spawnTarget(),
      loop: true,
    });

    // First target immediately
    this.spawnTarget();

    // Timer
    this.time.addEvent({
      delay: 1000,
      callback: () => {
        this.timeLeft -= 1000;
        if (this.timerText) {
          this.timerText.setText(`${Math.ceil(this.timeLeft / 1000)}s`);
        }
        if (this.timeLeft <= 0) {
          this.endGame();
        }
      },
      loop: true,
    });

    // Mouse move — update scope
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.playing) return;
      const scopeR = this.upgrades.scopeSize;
      if (this.scopeCircle) this.drawScopeCircle(pointer.x, pointer.y, scopeR);
      if (this.scopeCrosshair) this.drawCrosshair(pointer.x, pointer.y, scopeR);
    });

    // Click to shoot
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.playing) return;
      this.handleShoot(pointer.x, pointer.y);
    });
  }

  private endGame(): void {
    if (this.gameOver) return;
    this.gameOver = true;
    this.playing = false;
    this.cb.onGameOver(this.score);
    this.buildGameOverOverlay();
  }

  requestBegin(): void {
    // Wait for user click on start overlay
  }
}

export class SniperRange implements GameRuntime {
  private readonly config: GameRuntimeConfig;
  private readonly events: GameRuntimeEvents;
  private state: GameRuntimeState = 'idle';
  private score = 0;
  private game: Phaser.Game | null = null;
  private scene: SniperRangeScene | null = null;

  constructor(config: GameRuntimeConfig, events: GameRuntimeEvents = {}) {
    this.config = config;
    this.events = events;
  }

  mount(): void {
    if (this.state !== 'idle') return;

    this.scene = new SniperRangeScene({
      onShoot: () => this.events.onEvent?.('shoot', {}),
      onHit: (points: number) => {
        this.score += points;
        this.events.onScoreChange?.(this.score);
        this.events.onEvent?.('hit', { points });
      },
      onMiss: () => this.events.onEvent?.('miss', {}),
      onGameOver: (score: number) => {
        this.state = 'gameover';
        this.events.onGameOver?.(score, {});
      },
      onStart: () => {
        this.state = 'running';
        this.events.onStart?.();
      },
      onUpgrade: (type: string, level: number) => {
        this.events.onEvent?.('upgrade_purchase', { type, level });
      },
    });

    this.game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: this.config.parent,
      width: GAME_WIDTH,
      height: GAME_HEIGHT,
      backgroundColor: COLOR_BG,
      scale: {
        mode: Phaser.Scale.FIT,
        width: GAME_WIDTH,
        height: GAME_HEIGHT,
      },
      scene: [this.scene],
    });

    this.state = 'mounted';
  }

  start(): void {
    if (this.state === 'mounted' || this.state === 'paused' || this.state === 'gameover') {
      this.state = 'running';
      this.scene?.requestBegin();
    }
  }

  pause(): void {
    if (this.state !== 'running') return;
    this.state = 'paused';
    this.scene?.scene?.pause();
    this.events.onPause?.();
  }

  resume(): void {
    if (this.state !== 'paused') return;
    this.state = 'running';
    this.scene?.scene?.resume();
    this.events.onResume?.();
  }

  destroy(): void {
    if (this.state === 'destroyed') return;
    this.game?.destroy(true);
    this.game = null;
    this.scene = null;
    this.state = 'destroyed';
  }

  getScore(): number { return this.score; }
  getState(): GameRuntimeState { return this.state; }

  retry(): void {
    this.score = 0;
    this.events.onScoreChange?.(this.score);
    this.state = 'running';
    this.scene?.requestBegin();
  }
}

export default SniperRange;
